<template>
  <div class="footer-accordion">
    <div class="footer-accordion-item">
      <button
        id="footer-accordion-button-1"
        type="button"
        aria-expanded="false"
      >
        <span class="footer-accordion-title">Here To Help</span>
        <span class="icon" aria-hidden="true"></span>
      </button>
      <div class="footer-accordion-content">
        <a href="/help-centre">Help Centre</a>
        <a href="#" type="submit" @click="handleFAQClick">FAQs</a>
        <a v-if="features.showBettingLogos" href="/safer-gambling"
          >Safer Gambling</a
        >
      </div>
    </div>

    <div class="footer-accordion-item" v-if="features.showHowItWorks">
      <button
        id="footer-accordion-button-2"
        type="button"
        aria-expanded="false"
      >
        <span class="footer-accordion-title">How it Works</span>
        <span class="icon" aria-hidden="true"></span>
      </button>
      <div class="footer-accordion-content">
        <a href="/page/howtoshoponline">How To Shop Online</a>
      </div>
    </div>

    <div class="footer-accordion-item">
      <button
        id="footer-accordion-button-3"
        type="button"
        aria-expanded="false"
      >
        <span class="footer-accordion-title">Get Involved</span>
        <span class="icon" aria-hidden="true"></span>
      </button>
      <div class="footer-accordion-content">
        <a href="https://careers.rewards4group.com/" target="_blank" rel="noopener noreferrer">Careers</a>
      </div>
    </div>

    <div class="footer-accordion-item">
      <button
        id="footer-accordion-button-4"
        type="button"
        aria-expanded="false"
      >
        <span class="footer-accordion-title">Legal Stuff</span>
        <span class="icon" aria-hidden="true"></span>
      </button>
      <div class="footer-accordion-content">
        <a href="/terms">Member Agreement</a>
        <a href="/privacycentre">Privacy Policy</a>
        <a href="#" @click.prevent="openCookieSettings">Cookie Policy</a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, computed } from "vue";
import { useTenantConfig } from "../composables/useTenantConfig";
import ZendeskService from "../services/zendesk-service";
import { useAuthStore } from "../stores/auth";
import { showCookieSettings } from "../utils/global";

export default defineComponent({
  name: "FooterLinks",
  setup() {
    const { features } = useTenantConfig();
    const authStore = useAuthStore();
    const isAuthenticated = computed(() => authStore.isAuthenticated);

    // Open CookieFirst settings safely
    const openCookieSettings = () => {
      showCookieSettings();
    };

    // Zendesk FAQ page navigation
    const openZendeskFAQPage = async (e: Event, pageType: "faq") => {
      e.preventDefault();

      const targetUrl = await ZendeskService.getZendeskNavigationUrl(
        pageType,
        isAuthenticated.value
      );

      // Open the URL in a new tab
      const newWindow = window.open("about:blank", "_blank");
      if (newWindow) {
        newWindow.location = targetUrl;
      } else {
        alert(
          "Popup was blocked by the browser. Please allow popups for this site."
        );
      }
    };

    // Click handlers
    const handleFAQClick = (e: Event) => openZendeskFAQPage(e, "faq");

    // Accordion functionality
    onMounted(() => {
      const buttons = document.querySelectorAll(".footer-accordion button");

      function toggleAccordion(this: HTMLButtonElement) {
        const isExpanded = this.getAttribute("aria-expanded") === "true";
        buttons.forEach((b) => b.setAttribute("aria-expanded", "false"));
        this.setAttribute("aria-expanded", String(!isExpanded));
      }

      buttons.forEach((b) => b.addEventListener("click", toggleAccordion));
    });

    return {
      features,
      openCookieSettings,
      handleFAQClick,
    };
  },
});
</script>

<style scoped>
.footer-accordion .footer-accordion-item {
  border-bottom: 1px solid white;
}

button {
  position: relative;
  display: block;
  text-align: left;
  width: 100%;
  padding: 1rem 0;
  color: white;
  font-size: 18px;
  font-weight: 600;
  border: none;
  background: none;
  outline: none;
}

button:hover,
button:focus {
  cursor: pointer;
  color: white;
}

button[aria-expanded="true"] {
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 5px;
}

button[aria-expanded="true"] + .footer-accordion-content {
  opacity: 1;
  max-height: 100px;
  transition: all 200ms linear;
  will-change: opacity, max-height;
  margin-top: -10px;
}

.footer-accordion-content {
  display: grid;
  opacity: 0;
  max-height: 0;
  color: white;
  overflow: hidden;
  transition: opacity 200ms linear, max-height 200ms linear;
  will-change: opacity, max-height;
}

.footer-accordion-content a {
  color: white;
  font-size: 16px;
  margin-bottom: 0.5rem;
  text-decoration: none;
}

.icon {
  display: inline-block;
  position: absolute;
  top: 18px;
  right: 0;
  width: 22px;
  height: 22px;
  border: 1px solid;
  border-radius: 22px;
}

.icon::before {
  display: block;
  position: absolute;
  content: "";
  top: 9px;
  left: 5px;
  width: 10px;
  height: 2px;
  background: currentColor;
}
.icon::after {
  display: block;
  position: absolute;
  content: "";
  top: 5px;
  left: 9px;
  width: 2px;
  height: 10px;
  background: currentColor;
}

button[aria-expanded="true"] .icon::after {
  width: 0;
}

button[aria-expanded="true"] + .accordion-content {
  opacity: 1;
  max-height: 100%;
  transition: all 200ms linear;
  will-change: opacity, max-height;
}
</style>
